import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

function passwordRules(pw) {
  return {
    length: (pw || "").length >= 12,
    upper: /[A-Z]/.test(pw || ""),
    lower: /[a-z]/.test(pw || ""),
    digit: /\d/.test(pw || ""),
  };
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const rules = useMemo(() => passwordRules(pw), [pw]);
  const allPass = rules.length && rules.upper && rules.lower && rules.digit;
  const matches = pw && pw === pw2;
  const canSubmit = allPass && matches && !!token && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: pw,
      });
      setDone(true);
      setTimeout(() => nav("/admin/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FB] px-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-10 shadow-[0_24px_48px_-24px_rgba(10,37,64,0.18)]" data-testid="reset-missing-token">
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Invalid reset link</h1>
          <p className="mt-2 text-sm text-slate-500">
            This reset link is missing a token. Please request a new one from the login page.
          </p>
          <Link to="/forgot-password" className="mt-6 inline-block text-sm text-[#0A2540] hover:underline">
            Request a new link →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <aside className="hidden flex-col justify-between bg-[#0A2540] p-12 text-white lg:flex">
        <Link to="/" className="font-display text-lg font-bold tracking-tight">MedAdmits</Link>
        <div>
          <ShieldCheck className="h-10 w-10 text-white/80" />
          <h2 className="font-display mt-6 text-3xl font-semibold leading-tight tracking-tight">
            Choose a new password.
            <br />
            <span className="text-white/60">12+ chars · mix of upper, lower & digits.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm text-white/70">
            After resetting, you'll be signed out of any active sessions and
            asked to log in with your new password.
          </p>
        </div>
        <p className="text-xs text-white/50">© MedAdmits · Private MBBS Council</p>
      </aside>

      <main className="flex items-center justify-center bg-[#F6F8FB] px-6 py-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md rounded-2xl bg-white p-10 shadow-[0_24px_48px_-24px_rgba(10,37,64,0.18)]"
          data-testid="reset-form"
        >
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0A2540] text-white">
            <KeyRound className="h-5 w-5" />
          </div>
          <h1 className="font-display mt-4 text-2xl font-semibold tracking-tight text-[#0A2540]">
            Set a new password
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Pick something strong. You'll use this on every login.
          </p>

          {done ? (
            <div
              className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
              data-testid="reset-success"
            >
              <CheckCircle2 className="mb-2 h-5 w-5" />
              Password updated. Redirecting you to the login page…
            </div>
          ) : (
            <>
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="pw" className="text-sm">New password</Label>
                  <Input
                    id="pw"
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1.5 h-12"
                    data-testid="reset-new"
                  />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-sm">Confirm new password</Label>
                  <Input
                    id="pw2"
                    type="password"
                    required
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1.5 h-12"
                    data-testid="reset-confirm"
                  />
                </div>
              </div>

              <ul className="mt-4 space-y-1 text-xs">
                <RuleRow ok={rules.length} text="At least 12 characters" />
                <RuleRow ok={rules.upper} text="One uppercase letter" />
                <RuleRow ok={rules.lower} text="One lowercase letter" />
                <RuleRow ok={rules.digit} text="One digit" />
                <RuleRow ok={!!matches} text="Passwords match" />
              </ul>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="mt-6 h-12 w-full bg-[#0A2540] font-semibold text-white hover:bg-[#1E3A5F] disabled:opacity-50"
                data-testid="reset-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating…
                  </>
                ) : (
                  "Update password"
                )}
              </Button>
            </>
          )}
        </form>
      </main>
    </div>
  );
}

function RuleRow({ ok, text }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          ok ? "bg-emerald-500" : "bg-slate-300"
        }`}
      />
      {text}
    </li>
  );
}
